import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { baseBudgetMinimum, formatBudgetMinimum } from '@/utils/missingBaseBudget';
import type { MissingBaseBudgetCampaign } from '@/types/hourlyBudget';

export function MissingBaseBudgetPanel({
  items,
  onSetBase,
}: {
  items: MissingBaseBudgetCampaign[];
  onSetBase: (item: MissingBaseBudgetCampaign) => void;
}) {
  if (items.length === 0) return null;
  const count = items.length;
  return (
    <section className="overflow-hidden rounded-xl border border-warning/30 bg-surface">
      <div className="border-b border-warning/20 bg-warning-subtle px-4 py-3">
        <p className="text-sm font-medium text-warning-ink">
          {count} enabled {count === 1 ? 'campaign has' : 'campaigns have'} no 12am base budget
        </p>
        <p className="mt-1 text-sm text-warning-ink">This amount is applied at 12:00 AM IST.</p>
      </div>
      <div className="max-h-80 overflow-y-auto">
        <table className="min-w-full text-sm">
          <tbody>
            {items.map((item) => (
              <tr key={item.campaign_id} className="border-b border-border last:border-b-0">
                <td className="px-4 py-2 font-medium text-foreground">
                  {item.name || item.campaign_id}
                </td>
                <td className="px-4 py-2 text-foreground-muted">{item.product}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="secondary" onClick={() => onSetBase(item)}>
                    Set base budget
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function SetBaseBudgetDialog({
  item,
  countryCode,
  accountType,
  saving,
  error,
  onSave,
  onClose,
}: {
  item: MissingBaseBudgetCampaign;
  countryCode: string | null | undefined;
  accountType: string | null | undefined;
  saving: boolean;
  error: string | null;
  onSave: (amount: number) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const minimum = baseBudgetMinimum(item, countryCode, accountType);
  const minimumLabel = minimum == null ? null : formatBudgetMinimum(minimum);
  const sharedCount = item.schedule?.shared_campaign_count ?? 0;
  const name = item.name || item.campaign_id;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={saving ? undefined : onClose}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-surface p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-labelledby="set-base-budget-title"
      >
        <h3 id="set-base-budget-title" className="mb-2 text-base font-semibold text-foreground">
          Set 12am base budget
        </h3>
        <p className="mb-3 text-sm text-foreground-muted">
          {name}. This amount is applied at 12:00 AM IST.
        </p>
        {sharedCount > 1 ? (
          <p className="mb-3 text-sm text-foreground-muted">
            This saves the 12am base for all {sharedCount} campaigns on this schedule.
          </p>
        ) : null}
        {item.schedule && item.schedule.active === false ? (
          <p className="mb-3 text-sm text-warning-ink">
            This schedule is off. 12am will not apply this amount until it is turned on in the
            table.
          </p>
        ) : null}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const amount = Number(value);
            if (!value.trim() || Number.isNaN(amount)) {
              setLocalError('Enter a base budget.');
              return;
            }
            if (minimum != null && amount < minimum) {
              setLocalError(`Base budget must be at least ${minimumLabel}.`);
              return;
            }
            setLocalError(null);
            onSave(amount);
          }}
        >
          <label className="mb-1 block text-sm font-medium text-foreground" htmlFor="base-budget-amount">
            Base budget{minimumLabel ? ` (minimum ${minimumLabel})` : ''}
          </label>
          <input
            id="base-budget-amount"
            type="number"
            step="0.01"
            min={minimum ?? undefined}
            value={value}
            disabled={saving}
            aria-label="Base budget"
            onChange={(event) => setValue(event.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          />
          {localError || error ? (
            <p className="mt-2 text-sm text-error">{localError || error}</p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
